// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useCallback, useMemo, useState, useEffect } from 'react';

import {
  ActionIcon,
  Menu,
  type MenuItemProps,
  type ActionIconProps,
  type MantineSize,
  Flex,
  getSize,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { EvolveIcon, type EvolveIconName } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { DEFAULT_TRANSITIONS_DURATION } from 'styles/theme/useGenerateMantineTheme';
import { EVOLVEICON_SIZE_PREFIX } from 'styles/theme/variableResolver';

export type GridMenuProps<T extends string> = {
  options: readonly (
    | T
    | ({
        label?: ReactNode;
        value: T;
        icon?: EvolveIconName;
        iconSize?: MantineSize;
        hidden?: boolean;
      } & MenuItemProps)
  )[];
  hoverClass?: string;
  icon?: EvolveIconName;
  iconSize?: MantineSize;
  hideIcon?: boolean;
  tooltip?: string;
  target?: (toggleOpened: () => void) => ReactNode;
  /** If true, will keep the children mounted even when the menu isn't being actioned. */
  keepMounted?: boolean;
} & Pick<ActionIconProps, 'variant' | 'loading' | 'size' | 'disabled' | 'color'> &
  (
    | {
        /**
         * Callback when a menu item is selected.
         *
         * @param menuItemSelected the menu item selected
         * @returns `true` if the `menuItemSelected` was handled via `onMenuItemSelected`, `false` to leave it
         * selected so `children` can act on it
         */
        onMenuItemSelected?: (menuItemSelected: T) => boolean | Promise<boolean>;
        /**
         * Menu children. Called with the current menu item selected and a "reset selection" function.
         * Used to programmatically render something based on what item is currently selected.
         *
         * @param menuItemSelected The menu option currently selected
         * @param resetSelection Call to reset the menu after handling (ie, modal closed)
         */
        children: (menuItemSelected: T | undefined, resetSelection: () => void) => void;
      }
    | {
        /**
         * Callback when a menu item is selected.
         *
         * @param menuItemSelected the menu item selected
         */
        onMenuItemSelected: (menuItemSelected: T) => void | Promise<void>;
        children?: never;
      }
  );

/**
 * Mantine Menu wrapper that works well specifically in AG Grids,
 * though should generally be used in place of Mantine.Menu across the app.
 */
export const GridMenu = <T extends string>({
  children,
  options,
  onMenuItemSelected,
  hoverClass,
  icon = 'Menu',
  hideIcon = !!hoverClass,
  tooltip,
  size,
  iconSize = (typeof size === 'string' && size.startsWith('input-') ? size.replace('input-', '') : size) as MantineSize,
  loading,
  disabled,
  target,
  keepMounted: keepMountedParent,
  ...actionIconProps
}: GridMenuProps<T>) => {
  const [opened, { toggle, set: setOpened }] = useDisclosure(false);
  const [selected, setSelected] = useState<T>();
  const resetSelection = useCallback(() => setSelected(undefined), []);
  const filteredOptions = useMemo(() => options.filter((o) => typeof o === 'string' || !o.hidden), [options]);
  const [saving, setSaving] = useState(false);
  const [keepMounted, setKeepMounted] = useState(false);

  // Only mount the menu children if we have any chance of needing them
  // (meaning, the menu is open, or we selected a menu item that hasn't yet resolved)
  useEffect(() => {
    if (opened || isNotNil(selected)) {
      setKeepMounted(true);
      return undefined;
    }
    const timeout = setTimeout(() => {
      setKeepMounted(false);
    }, DEFAULT_TRANSITIONS_DURATION + 1);
    return () => clearTimeout(timeout);
  }, [opened, selected]);

  if (filteredOptions.length === 0) return null;
  return (
    <Flex align="center" justify="center">
      <Menu opened={opened} onChange={setOpened} position="bottom-end">
        <Menu.Target>
          <AgGridStyleTooltip label={tooltip} withArrow hidden={!tooltip || opened}>
            {target?.(toggle) ?? (
              <ActionIcon
                onClick={(e) => {
                  if (!disabled) toggle();
                  e.stopPropagation();
                }}
                color="gray.6"
                size={size}
                disabled={disabled}
                loading={loading || saving}
                variant="subtle"
                {...actionIconProps}
              >
                <EvolveIcon
                  className={
                    opened || isNotNil(selected) || !hideIcon ? undefined : (hoverClass ?? 'show-on-row-hover')
                  }
                  icon={icon}
                  size={iconSize}
                />
              </ActionIcon>
            )}
          </AgGridStyleTooltip>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          {filteredOptions.map((opt) => {
            const {
              value,
              label = value,
              icon: optionIcon = null,
              iconSize: optionIconSize,
              ...rest
            } = typeof opt === 'string' ? { value: opt } : opt;
            return (
              <Menu.Item
                key={value}
                onClick={async (e) => {
                  setSelected(value);
                  setSaving(true);
                  try {
                    const handledBySelection = await onMenuItemSelected?.(value);
                    if (isNil(children) || handledBySelection === true) {
                      resetSelection();
                    }
                  } finally {
                    setSaving(false);
                  }
                  e.stopPropagation();
                }}
                leftSection={
                  optionIcon ? (
                    <Flex justify="center" align="center" w={getSize('md', EVOLVEICON_SIZE_PREFIX, false)}>
                      <EvolveIcon icon={optionIcon} size={optionIconSize} />
                    </Flex>
                  ) : undefined
                }
                // Automatically make "Delete" options red
                color={typeof label === 'string' && label.toLowerCase().includes('delete') ? 'red' : undefined}
                {...rest}
              >
                {label}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
      {(keepMounted || keepMountedParent) && (children?.(selected, resetSelection) ?? null)}
    </Flex>
  );
};
